export const EXPERIENCE_CONTRACT_VERSION = 1;

const text = (value) => String(value ?? '').trim();
const list = (value) => (Array.isArray(value) ? value : []);

export const normalizeRecordedText = (value) => (
  String(value ?? '').replace(/\r\n?/g, '\n').trim()
);

const utf8Bytes = (value) => {
  const bytes = [];
  for (const symbol of value) {
    const codePoint = symbol.codePointAt(0);
    if (codePoint <= 0x7f) {
      bytes.push(codePoint);
    } else if (codePoint <= 0x7ff) {
      bytes.push(0xc0 | (codePoint >>> 6), 0x80 | (codePoint & 0x3f));
    } else if (codePoint <= 0xffff) {
      bytes.push(
        0xe0 | (codePoint >>> 12),
        0x80 | ((codePoint >>> 6) & 0x3f),
        0x80 | (codePoint & 0x3f),
      );
    } else {
      bytes.push(
        0xf0 | (codePoint >>> 18),
        0x80 | ((codePoint >>> 12) & 0x3f),
        0x80 | ((codePoint >>> 6) & 0x3f),
        0x80 | (codePoint & 0x3f),
      );
    }
  }
  return bytes;
};

const rotateRight = (value, amount) => ((value >>> amount) | (value << (32 - amount))) >>> 0;

export const sha256Text = (value) => {
  const constants = [
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1,
    0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
    0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786,
    0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147,
    0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
    0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b,
    0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a,
    0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
    0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
  ];
  const hash = [
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a,
    0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ];
  const bytes = utf8Bytes(String(value ?? ''));
  const bitLength = bytes.length * 8;
  bytes.push(0x80);
  while ((bytes.length % 64) !== 56) bytes.push(0);
  const high = Math.floor(bitLength / 0x100000000);
  const low = bitLength >>> 0;
  for (let shift = 24; shift >= 0; shift -= 8) bytes.push((high >>> shift) & 0xff);
  for (let shift = 24; shift >= 0; shift -= 8) bytes.push((low >>> shift) & 0xff);

  for (let offset = 0; offset < bytes.length; offset += 64) {
    const words = new Array(64).fill(0);
    for (let index = 0; index < 16; index += 1) {
      const start = offset + (index * 4);
      words[index] = (
        (bytes[start] << 24)
        | (bytes[start + 1] << 16)
        | (bytes[start + 2] << 8)
        | bytes[start + 3]
      ) >>> 0;
    }
    for (let index = 16; index < 64; index += 1) {
      const previous15 = words[index - 15];
      const previous2 = words[index - 2];
      const sigma0 = rotateRight(previous15, 7) ^ rotateRight(previous15, 18) ^ (previous15 >>> 3);
      const sigma1 = rotateRight(previous2, 17) ^ rotateRight(previous2, 19) ^ (previous2 >>> 10);
      words[index] = (words[index - 16] + sigma0 + words[index - 7] + sigma1) >>> 0;
    }

    let [a, b, c, d, e, f, g, h] = hash;
    for (let index = 0; index < 64; index += 1) {
      const sum1 = rotateRight(e, 6) ^ rotateRight(e, 11) ^ rotateRight(e, 25);
      const choice = (e & f) ^ ((~e) & g);
      const temp1 = (h + sum1 + choice + constants[index] + words[index]) >>> 0;
      const sum0 = rotateRight(a, 2) ^ rotateRight(a, 13) ^ rotateRight(a, 22);
      const majority = (a & b) ^ (a & c) ^ (b & c);
      const temp2 = (sum0 + majority) >>> 0;
      h = g;
      g = f;
      f = e;
      e = (d + temp1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (temp1 + temp2) >>> 0;
    }
    [a, b, c, d, e, f, g, h].forEach((word, index) => {
      hash[index] = (hash[index] + word) >>> 0;
    });
  }

  return hash.map((word) => word.toString(16).padStart(8, '0')).join('');
};

export const recordedTextHash = (value) => sha256Text(normalizeRecordedText(value));

export const updateShadowRecordedText = (shadow, recordedText) => {
  const previousHash = recordedTextHash(shadow?.recorded_text);
  const nextHash = recordedTextHash(recordedText);
  return {
    ...(shadow || {}),
    recorded_text: recordedText,
    recorded_text_hash: nextHash,
    ...(previousHash === nextHash ? {} : {
      coverage_review_status: 'draft',
      reviewed_text_hash: '',
    }),
  };
};

const normalizedStrings = (values) => list(values).map(text).filter(Boolean).sort();
export const EVIDENCE_ROLE_KEYS = [
  'hook',
  'explanation',
  'human_connection',
  'consequence',
  'observable_cue',
];

const normalizedEvidenceRoles = (roles) => Object.fromEntries(
  EVIDENCE_ROLE_KEYS
    .map((key) => [key, normalizedStrings(roles?.[key])])
    .filter(([, values]) => values.length > 0),
);

const normalizedLens = (lens) => ({
  lens_id: text(lens?.lens_id),
  canonical_topic_weights: Object.fromEntries(
    Object.entries(lens?.canonical_topic_weights || {})
      .map(([key, value]) => [text(key), Number(value)])
      .filter(([key, value]) => key && Number.isFinite(value))
      .sort(([left], [right]) => left.localeCompare(right)),
  ),
  central_question: text(lens?.central_question),
  experience_contribution: text(lens?.experience_contribution),
  claim_refs: normalizedStrings(lens?.claim_refs),
  depth: text(lens?.depth),
  fit_reason: text(lens?.fit_reason),
});

export const opportunityReviewInputHash = (opportunity, guideShadow) => {
  const anchorIds = normalizedStrings(opportunity?.anchor_stop_ids);
  const shadowsByStop = new Map(
    list(guideShadow).map((shadow) => [text(shadow?.stop_id), shadow]),
  );
  const coverage = anchorIds.map((stopId) => {
    const shadow = shadowsByStop.get(stopId) || {};
    return {
      stop_id: stopId,
      recorded_text_hash: recordedTextHash(shadow.recorded_text),
      coverage_review_status: text(shadow.coverage_review_status) || 'draft',
      semantic_summary: text(shadow.semantic_summary),
      claim_refs: normalizedStrings(shadow.claim_refs),
      anecdotes: normalizedStrings(shadow.anecdotes),
      arguments: normalizedStrings(shadow.arguments),
      main_reveal: text(shadow.main_reveal),
      avoid_repeating: normalizedStrings(shadow.avoid_repeating),
      coverage_exception_reason: text(shadow.coverage_exception_reason),
    };
  });
  return sha256Text(JSON.stringify({
    experience_contribution: text(opportunity?.experience_contribution),
    central_question: text(opportunity?.central_question),
    claim_refs: normalizedStrings(opportunity?.claim_refs),
    evidence_roles: normalizedEvidenceRoles(opportunity?.evidence_roles),
    personalization_mode: text(opportunity?.personalization_mode),
    visitor_lenses: list(opportunity?.visitor_lenses)
      .map(normalizedLens)
      .sort((left, right) => left.lens_id.localeCompare(right.lens_id)),
    anchor_stop_ids: anchorIds,
    coverage,
  }));
};
